/**
 * The per-document-type configuration DocumentPrintPage renders from.
 *
 * These live here, apart from the renderer, because the PUBLIC paper
 * (`/d/:token`) has no route params to read a config from — it resolves one by
 * `docType` off the server DTO. Keeping exactly one definition of each config is
 * what stops the customer's copy drifting from the staff copy: same title key,
 * same party label, same status namespace, by construction.
 *
 * The wrappers (SalesOrderPrintPage, …) are thin `<DocumentPrintPage config={…}>`
 * shells over these objects.
 */
import type { DocPrintConfig } from './DocumentPrintPage.js';

export const SALES_ORDER_DOC_CONFIG: DocPrintConfig = {
  endpoint: '/sales-orders',
  backPath: '/sales/orders',
  titleKey: 'salesOrders.documentTitle',
  partyLabelKey: 'invoices.printMeta.billTo',
  statusPrefix: 'salesOrders.status',
  // Customer-facing: a QR of the public /d/<token> paper. Minted through the
  // sales-orders route, NOT /cms/orders — that one only serves channel='online',
  // so a staff- or mandob-keyed order used to print no QR at all.
  publicLinkEndpoint: '/sales-orders',
  // Same link, pushed to the customer's phone instead of printed.
  whatsappDocType: 'salesOrder',
};

export const INVOICE_DOC_CONFIG: DocPrintConfig = {
  endpoint: '/invoices',
  backPath: '/sales/invoices',
  titleKey: 'invoices.documentTitle',
  partyLabelKey: 'invoices.printMeta.billTo',
  statusPrefix: 'invoices.status',
  // Customer-facing: the paper carries a QR of the public /d/<token> view.
  publicLinkEndpoint: '/invoices',
  whatsappDocType: 'invoice',
};

export const QUOTATION_DOC_CONFIG: DocPrintConfig = {
  endpoint: '/quotations',
  backPath: '/quotations',
  titleKey: 'quotations.documentTitle',
  partyLabelKey: 'invoices.printMeta.billTo',
  statusPrefix: 'quotations.status',
};

// SUPPLIER documents: no publicLinkEndpoint, ever. Their public link would show
// purchasing data (costs, suppliers) to anyone the printed code is shown to.
export const BILL_DOC_CONFIG: DocPrintConfig = {
  endpoint: '/bills',
  backPath: '/purchasing/bills',
  titleKey: 'bills.documentTitle',
  partyLabelKey: 'bills.printMeta.billFrom',
  statusPrefix: 'bills.status',
};

/**
 * A POS sale on the same paper — the register's "print before pay". Staff-only
 * by construction: no publicLinkEndpoint (an unpaid parked sale must never mint
 * a shareable no-login link that outlives the sale), no WhatsApp.
 */
export const POS_SALE_DOC_CONFIG: DocPrintConfig = {
  endpoint: '/pos/sales-doc',
  backPath: '/pos/register',
  titleKey: 'posSale.documentTitle',
  partyLabelKey: 'invoices.printMeta.billTo',
  statusPrefix: 'posSale.status',
};

/**
 * The document types the public paper can render, keyed by the `docType` the
 * API returns. Only customer-facing documents are ever mintable, so this map is
 * deliberately narrower than the configs above.
 */
export const DOC_PRINT_CONFIGS: Record<'sales_order' | 'invoice', DocPrintConfig> = {
  sales_order: SALES_ORDER_DOC_CONFIG,
  invoice: INVOICE_DOC_CONFIG,
};
